import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import salmonImage from './blogimg/vitaminD/salmon.jpg';
import eggsImage from './blogimg/vitaminD/eggs.jpg';
import fortifiedMilkImage from './blogimg/vitaminD/fortifiedMilk.jpg';
import cheeseImage from './blogimg/vitaminD/cheese.jpg';
import mushroomsImage from './blogimg/vitaminD/mushrooms.jpg';
import vitaminD3SupplementImage from './blogimg/vitaminD/vitaminD3.jpg';

// Helper to resolve local images
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'salmon.jpg':
      return salmonImage;
    case 'eggs.jpg':
      return eggsImage;
    case 'fortifiedmilk.jpg': // changed from fortifiedMilk.jpg
      return fortifiedMilkImage;
    case 'cheese.jpg':
      return cheeseImage;
    case 'mushrooms.jpg':
      return mushroomsImage;
    case 'vitamind3.jpg':     // changed from vitaminD3.jpg
      return vitaminD3SupplementImage;
    default:
      return null;
  }
}

const VitaminDFoodsPage = () => {
  const [article, setArticle] = useState(null);
  // UNUSED (commented to silence ESLint no-unused-vars):
  // const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Top Vitamin D Foods: Boost Bone Health & Immunity | Pure Health Nexus";

    // Helper function to add/update a meta tag
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Add/update standard meta tags
    setMetaTag("description", "Discover the best vitamin D-rich foods—like salmon, eggs, and fortified milk—to support bone health and immunity. Learn how to boost your vitamin D intake naturally.");
    setMetaTag("keywords", "Vitamin D, vitamin D foods, bone health, immune support, salmon, eggs, fortified milk, Pure Health Nexus");

    // Add/update Open Graph tags
    setMetaTag("og:title", "Top Vitamin D Foods for Bone Health & Immunity | Pure Health Nexus", true);
    setMetaTag("og:description", "Boost your vitamin D levels with top dietary sources and supplements for overall wellness.");
    setMetaTag("og:image", "https://purehealthnexus.com/assets/images/salmon.jpg", true);
    setMetaTag("og:url", "https://purehealthnexus.com/top-vitamin-d-foods", true);

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Optional cleanup on unmount
    return () => {
      document.title = "Pure Health Nexus"; // Reset title
      // If you want to remove meta tags, you can query & remove them here
    };
  }, []);

  // 2️⃣ Fetch Your Article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Firestore path: /products/blog/vitamin
        const colRef = collection(db, 'products', 'blog', 'vitamin');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          // UNUSED (commented to silence ESLint no-unused-vars):
          // setTimestamp(docData.eutimestamp);
        } else {
          console.warn('No blog document found for vitamin');
        }
      } catch (error) {
        console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Helper to Render Blocks from Firestore
  const renderBlock = (block, index) => {
    const { tag, text } = block;

    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1">
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2">
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3">{text}</h3>
          </React.Fragment>
        );
      case 'h4':
        return <h4 key={index}>{text}</h4>;
      case 'p':
        return (
          <p key={index} className="recipe-p">
            {text}
          </p>
        );
      case 'img':
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      case 'ul':
        return (
          <ul key={index} className="recipe-ul">
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol">
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === "object") {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return <div key={index}>{text}</div>;
    }
  };

  // UNUSED (commented to silence ESLint no-unused-vars):
  /*
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  */

  // 4️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {/*
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated on: {formatTimestamp(timestamp)}
          </p>
        )}
        */}
        {article.sections &&
          article.sections.map((section, sectionIndex) => (
            <section key={sectionIndex}>
              {section.blocks &&
                section.blocks.map((block, blockIndex) =>
                  renderBlock(block, blockIndex)
                )}
            </section>
          ))}
        <button
          className="recipe-button"
          onClick={() =>
            (window.location.href =
              'https://purehealthnexus.com/top-rated-vitamins')
          }
        >
          See Quality Vitamins Here
        </button>
      </div>
    </div>
  );
};

export default VitaminDFoodsPage;
